<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import LogoReferences from "@components/LogoReferences.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { PartnerPlacement } from "@core/schema/PartnerPlacement.js";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getClientsPath } from "@core/schema/paths/getClientsPath.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsHR } from "../data/getFaqsHR.js";
	import { getHRSolutions } from "../data/getHRSolutions.js";
	import { getPartnerLogos } from "../data/getPartnerLogos.js";
	import { getReasonsHR } from "../data/getReasonsHR.js";
	import { ReferencedClients } from "../data/getReferences.js";
	import { getSolutionsHR } from "../data/getSolutionsHR.js";
	import type { HRProps } from "./HRProps.ts";

	const { tenant } = getGlobalTemplateProps();
	const { partners }: HRProps = $props();
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="cg[[Pomůžeme]] vašemu HR oddělení cg[[vyhrát]] boj o talenty i cg[[snížit]] náklady" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="V závodu o nejlepší posily do týmu sp[[vítězí]] firmy, které naplno využívají sp[[moderní technologie]]. Softwarem nebo jiným řešením na míru vám pomůžeme sp[[snížit náklady]] na inzerci a provoz, sp[[zvýšit úspěšnost]] náboru nebo sp[[zkrátit onboarding]]."
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Chci fastest služby" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		background={Background.LightGradient}
		paddingFollowNext
		gridHeadline="container-8"
		gridDesc="container-6"
		centered
	>
		{#snippet headline()}
			<Formatted text="cg[[Zrychlíte]] zveřejňování inzerátů nebo cg[[získáte]] přehledná data o úspěchu náboru" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Nebaví vás ručně zadávat inzeráty]] na každý pracovní portál zvlášť? Potřebujete zjistit, sp[[jak se vaší firmě daří s náborem]], ale nechcete sázet na domněnky? Nebo zkrátka potřebujete vytvořit sp[[komplexní HR systém?]]"
				/>
			</p>
			<p>
				<Formatted
					text="sp[[Tým Fastest se o to rád postará]]. Díky sp[[řešení na míru]] mimo jiné snížíte náklady související s náborem nebo zaměstnancům na HR oddělení sp[[ušetříte až desítky hodin měsíčně]], které by jinak věnovali rutinní práci."
				/>
			</p>
		{/snippet}
	</Section>

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[Které]] HR problémy za vás cg[[vyřešíme]]" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getSolutionsHR(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Zeptat se na podrobnosti
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section
		background={Background.LightGradient}
		paddingFollowNext
		gridHeadline="container-8"
		gridDesc="container-6"
		centered
	>
		{#snippet headline()}
			<Formatted text="Komu HR cg[[řešení na míru]] nejčastěji dodáváme" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getHRSolutions(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} indexNumber={index + 1} />
			{/each}
		</div>
	</Section>

	<Section gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="Tymbe má cg[[pod jednou střechou]] HR i účetnictví" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.Hr} />
		</div>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getReferencePath(tenant)}
			>Zobrazit více
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section background={Background.LightGradient} centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="Komu dalšímu jsme cg[[pomohli?]]" />
		{/snippet}
	</Section>
	<LogoReferences logos={getPartnerLogos(partners, PartnerPlacement.HR)} />

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[5 důvodů]], proč digitalizaci a automatizaci HR řešit s Fastest" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getReasonsHR(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci nezávaznou konzultaci
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k řešením v oblasti cg[[HR]]" />
		{/snippet}
		<FaqItems faqs={getFaqsHR(tenant)} />
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
